import { twcn } from '@web/ui/core/utils';

/**
 * Key-value section vocabulary — a titled card (`Section`) holding label/value
 * `Row`s, with `Mono` for monospace ids and `Dash` for absent values. Lifted
 * from the card-detail identity tab so a second consumer (the customer Perfil
 * tab) renders the same key-value blocks without duplicating the markup.
 */
export function Section({
  title,
  children,
}: Readonly<{
  title: string;
  children: React.ReactNode;
}>): React.JSX.Element {
  return (
    <section className="rounded-md border border-border-soft bg-surface">
      <header className="border-b border-border-soft px-3 py-2 font-mono text-tiny uppercase tracking-eyebrow text-muted-foreground">
        {title}
      </header>
      <div className="flex flex-col">{children}</div>
    </section>
  );
}

export function Row({
  label,
  value,
  multiline = false,
}: Readonly<{ label: string; value: React.ReactNode; multiline?: boolean }>): React.JSX.Element {
  return (
    <div
      className={twcn(
        'flex gap-3 px-3 py-2 border-b border-border-soft last:border-b-0',
        multiline ? 'items-start' : 'items-center',
      )}
    >
      <span className="w-40 flex-shrink-0 text-meta text-muted-foreground">{label}</span>
      <span
        className={twcn(
          'flex-1 text-base text-foreground min-w-0',
          multiline ? 'break-words' : 'truncate',
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function Mono({ children }: Readonly<{ children: React.ReactNode }>): React.JSX.Element {
  return <span className="font-mono text-card-id tabular-nums">{children}</span>;
}

export function Dash(): React.JSX.Element {
  return <span className="text-faint-foreground">—</span>;
}
